import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FilterX, Search } from "lucide-react";
import { ApiDataProps } from "@/pages/Tenants";
import { LogProps } from "@/types/log";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface FiltersProps {
  updateFront: React.Dispatch<React.SetStateAction<ApiDataProps<LogProps>>>;
  setState: React.Dispatch<React.SetStateAction<string>>;
  funcSearch: (params: any) => Promise<any>;
}

export function LogFilters({ updateFront, setState, funcSearch }: FiltersProps) {
  const [route, setRoute] = useState("");
  const [method, setMethod] = useState("all");
  const [statusCode, setStatusCode] = useState("");
  const [type, setType] = useState("all");
  const [dateStart, setDateStart] = useState<Date | undefined>(undefined);
  const [dateEnd, setDateEnd] = useState<Date | undefined>(undefined);
  
  const handleSearch = async () => {
    setState("loading");
    
    const filters: {
      route?: string;
      method?: string;
      status_code?: number;
      type?: string;
      date_start?: string;
      date_end?: string;
    } = {};
    
    if (route) filters.route = route;
    if (method && method !== "all") filters.method = method;
    if (statusCode && !isNaN(parseInt(statusCode))) filters.status_code = parseInt(statusCode);
    if (type && type !== "all") filters.type = type;
    
    if (dateStart) {
      filters.date_start = format(dateStart, "yyyy-MM-dd");
    }
    
    if (dateEnd) {
      filters.date_end = format(dateEnd, "yyyy-MM-dd");
    }
    
    try {
      const { data } = await funcSearch(filters);
      updateFront(data.response);
    } catch (error) {
      console.error("Error searching logs:", error);
    } finally {
      setState("production");
    }
  };
  
  const handleClearFilters = () => {
    setRoute("");
    setMethod("all");
    setStatusCode("");
    setType("all");
    setDateStart(undefined);
    setDateEnd(undefined);
    
    // Reload without filters
    handleSearch();
  };
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="space-y-2">
          <Label htmlFor="route">Recurso</Label>
          <Input
            id="route"
            value={route}
            onChange={(e) => setRoute(e.target.value)}
            placeholder="Ex: transações, usuários"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="method">Tipo de Operação</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas Operações</SelectItem>
              <SelectItem value="GET">Consulta</SelectItem>
              <SelectItem value="POST">Criação</SelectItem>
              <SelectItem value="PUT">Atualização</SelectItem>
              <SelectItem value="DELETE">Exclusão</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="status">Código de Status</Label>
          <Input
            id="status"
            value={statusCode}
            onChange={(e) => setStatusCode(e.target.value)}
            placeholder="Ex: 200, 404"
            type="number"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="type">Categoria</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas Categorias</SelectItem>
              <SelectItem value="action">Ação do Usuário</SelectItem>
              <SelectItem value="error">Problema</SelectItem>
              <SelectItem value="auth">Autenticação</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>Data inicial</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !dateStart && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateStart ? (
                  format(dateStart, "dd/MM/yyyy", { locale: ptBR })
                ) : (
                  <span>Selecione</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={dateStart}
                onSelect={setDateStart}
                initialFocus
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
        </div>
        
        <div className="space-y-2">
          <Label>Data final</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !dateEnd && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateEnd ? (
                  format(dateEnd, "dd/MM/yyyy", { locale: ptBR })
                ) : (
                  <span>Selecione</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={dateEnd}
                onSelect={setDateEnd}
                initialFocus
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
      
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={handleClearFilters}
          className="h-9"
        >
          <FilterX className="h-4 w-4 mr-2" />
          Limpar filtros
        </Button>
        <Button
          onClick={handleSearch}
          className="h-9 bg-blue-600 hover:bg-blue-700"
        >
          <Search className="h-4 w-4 mr-2" />
          Filtrar
        </Button>
      </div>
    </div>
  );
} 